//Users/kacey/BoxyQR_Website/BoxyQR/boxyQR_website/client/src/components/modals/CancelSubscriptionModal.js

import React from 'react';
import PropTypes from 'prop-types';

const CancelSubscriptionModal = ({ 
  showCancelModal, 
  setShowCancelModal, 
  account, 
  accountId, 
  processingPayment, 
  setProcessingPayment, 
  setError,
  onCanceled
}) => {
  // Calculate days remaining with better error handling
  let daysRemaining = 30; // Default to 30 days as fallback
  
  try {
    if (account?.subscription?.currentPeriodEnd) {
      const endDate = new Date(account.subscription.currentPeriodEnd);
      const currentDate = new Date();
      
      // Log the dates for debugging
      console.log('Modal calculation:', {
        endDate: endDate.toISOString(),
        currentDate: currentDate.toISOString(),
        difference: endDate - currentDate
      });
      
      const timeDiff = endDate - currentDate;
      if (timeDiff > 0) {
        daysRemaining = Math.max(1, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
      }
      console.log('Calculated days remaining:', daysRemaining);
    } else {
      console.log('No currentPeriodEnd found in account data:', account);
    }
  } catch (error) {
    console.error('Error calculating days remaining:', error);
  }
  
  // Handle the cancel process
  const handleCancelSubscription = async () => {
    setProcessingPayment(true);
    
    try {
      // Update the subscription status directly
      const response = await fetch('/api/account/update-subscription-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: accountId,
          status: 'downgraded',
          updateStripe: true
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      
      // Clear any existing errors
      if (setError) {
        setError(null);
      }
      
      // Close the modal
      setShowCancelModal(false);
      
      // Reset processing state - IMPORTANT!
      setProcessingPayment(false);
      
      // Call the onCanceled callback to refresh the account data
      if (onCanceled) {
        onCanceled();
      }
      
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError(error.message || 'Failed to cancel subscription');
      setProcessingPayment(false); // Reset here too!
      setShowCancelModal(false);
    }
  };
  
  if (!showCancelModal) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Cancel Subscription Confirmation</h3>
        <p className="mb-4">Are you sure you want to cancel your subscription?</p>
        
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You will continue to have access to your current plan for {daysRemaining} more {daysRemaining === 1 ? 'day' : 'days'}.
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                After that period, your plan will be downgraded and your data will be preserved, but premium features will no longer be available.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button 
            onClick={() => {
              setShowCancelModal(false);
              setProcessingPayment(false); // Reset state when closing
            }}
            className="px-4 py-2 border border-gray-300 rounded shadow-sm text-gray-700 hover:bg-gray-50"
            disabled={processingPayment}
          >
            Keep Subscription
          </button>
          <button 
            onClick={handleCancelSubscription}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded shadow-sm"
            disabled={processingPayment}
          >
            {processingPayment ? 'Processing...' : 'Cancel Subscription'}
          </button>
        </div>
      </div>
    </div>
  );
};

CancelSubscriptionModal.propTypes = {
  showCancelModal: PropTypes.bool.isRequired,
  setShowCancelModal: PropTypes.func.isRequired,
  account: PropTypes.object,
  accountId: PropTypes.string,
  processingPayment: PropTypes.bool.isRequired,
  setProcessingPayment: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  onCanceled: PropTypes.func
};

export default CancelSubscriptionModal;